import type { Security } from '@src/types/Security';
import { toBig } from '@src/utils/number';
import { getPricingReference } from '@src/utils/pricingMode';

import type { ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { get, isFunction, isObject } from 'lodash-es';
import { OrderStrategy } from '../../../../types/OrderStrategy';
import { PricingModeEnum, SubProductTypeEnum } from '../../../../types/types';
import { getConfirmParam, isPriceForSecurityRate } from '../../../../utils/confirmUtils';
import { formatUSTreasuryPrice, isUSTreasurySecurity } from '../../../../utils/usTreasuryPrice';
import { AgGridPrice } from '../../../AgGrid/AgGridPrice';
import type { PriceValue } from '../../aggFuncs/priceAggFunc';
import { BlotterAggFunc } from '../../aggFuncs/registeredAggFuncs';
import type { BlotterTableAgGridContext } from '../../BlotterTableContext';
import { baseColumn } from '../baseColumn';
import type { ColDefFactory, Column } from '../types';
import { numericColumnComparator } from '../utils';
import type { PriceParams } from './price.types';

export const price: ColDefFactory<Column<PriceParams>> = column => ({
  initialWidth: 150,
  minWidth: 70,
  maxWidth: 210,
  ...baseColumn(column),
  type: 'numericColumn',
  cellEditor: 'input',

  cellRenderer: ({ value, data, colDef, context }: ICellRendererParams<unknown, PriceValue>) => {
    return <AgGridPrice value={value} data={data} colDef={colDef} context={context} {...column.params} />;
  },
  comparator: (a: PriceValue, b: PriceValue) => {
    return numericColumnComparator(a?.price, b?.price);
  },
  headerClass: () => {
    const columnGroupClass = column.columnGroup ? 'ag-custom-column-group' : '';
    return column.params?.align !== 'left' ? `ag-right-aligned-header ${columnGroupClass}` : `${columnGroupClass}`;
  },
  cellClass: () => {
    return column.params?.align !== 'left' ? 'ag-right-aligned-cell' : '';
  },
  // There is no official type provided for this callback I could find -- so typing the parameters of this callback manually for now.
  valueParser: ({ oldValue, newValue }: { oldValue: PriceValue; newValue: string | undefined }): PriceValue => {
    try {
      const newValueBig = toBig(newValue);
      if (!newValueBig) {
        return undefined;
      }

      return {
        asset: oldValue?.asset,
        quoteCurrency: oldValue?.quoteCurrency,
        aggregate: true,
        price: newValueBig,
        endPrice: oldValue?.endPrice,
        isBestPrice: false,
        shouldFormatAsUSTreasury: oldValue?.shouldFormatAsUSTreasury,
        confirm: oldValue?.confirm,
        isRate: oldValue?.isRate,
      };
    } catch (e) {
      return oldValue;
    }
  },
  valueGetter: ({ data, context }: ValueGetterParams<unknown>): PriceValue => {
    if (!data) {
      return undefined;
    }

    const priceField: string | undefined = column.field;
    const price: string | undefined = priceField ? get(data, priceField) : undefined;

    const isBestPrice = column.params?.getIsBestPrice?.(data) ?? false;
    // Sometimes a price is relative (a reference price) and not absolute!
    let isReferencePrice = column.params?.isReferencePrice ?? false;
    if (isFunction(column.params?.isReferencePrice)) {
      isReferencePrice = column.params.isReferencePrice(data);
    }
    if (isReferencePrice) {
      if (get(data, 'PricingMode') === PricingModeEnum.Absolute) {
        return undefined;
      }
      const referencePrice = getPricingReference(data, priceField);
      return {
        price: toBig(referencePrice?.value),
        quoteCurrency: referencePrice?.currency,
        endPrice: undefined,
        aggregate: false,
        asset: undefined,
        isBestPrice,
        shouldFormatAsUSTreasury: false,
        confirm: undefined, // confirms are not relevant for reference price values
        isRate: false, // rates are not relevant for reference prices
      };
    }

    const { asset: inputAsset, assetField, quoteCurrencyField } = column.params ?? {};
    const resolvedAsset = inputAsset ?? (assetField ? get(data, assetField) : undefined);

    const maybeSecurity = resolvedAsset ? context.current.securitiesBySymbol?.get(resolvedAsset) : undefined;
    const maybeCurrency = resolvedAsset ? context.current.currenciesBySymbol?.get(resolvedAsset) : undefined;
    const asset = maybeSecurity?.Symbol ?? maybeCurrency?.Symbol;
    const quoteCurrency = getQuoteCurrency({ data, context, security: maybeSecurity, quoteCurrencyField });

    // price band display eg 1000-2000
    let endPrice: string | undefined = undefined;
    const strategy = context.current.strategiesByName?.get(get(data, 'Strategy', ''));
    if (column.params?.showPriceAsRange && strategy && strategy instanceof OrderStrategy && strategy.showPriceAsRange) {
      endPrice = get(data, `Parameters.${strategy.endPriceParamName}`);
    }

    // treasury formatting resolution and decision
    const formatAssetField = column.params?.detectUSTreasuryFormat?.assetField;
    const formatAsset: string | undefined = formatAssetField ? get(data, formatAssetField) : undefined;
    const formatSecurity = formatAsset ? context.current.securitiesBySymbol?.get(formatAsset) : undefined;
    // If either the main security (via assetField) or the detectUSTreasuryFormat.assetField point at a GovernmentBond security, we format as a US Treasury!
    const shouldFormatAsUSTreasury = isUSTreasurySecurity(formatSecurity) || isUSTreasurySecurity(maybeSecurity);

    return {
      price: toBig(price),
      endPrice: toBig(endPrice),
      quoteCurrency,
      asset,
      aggregate: true,
      isBestPrice,
      shouldFormatAsUSTreasury,
      isRate: isPriceForSecurityRate(maybeSecurity),
      confirm: getConfirmFromData({
        data,
        security: maybeSecurity,
        confirmParamsPath: column.params?.confirmParamsPath,
      }),
    };
  },
  // used for exports since we have implemented a cell renderer of our own
  valueFormatter: ({ value }: ValueFormatterParams<unknown, PriceValue>) => {
    if (!value || !value.price) {
      return '';
    }

    if (value.shouldFormatAsUSTreasury) {
      return formatUSTreasuryPrice(value.price);
    }

    if (value.isRate) {
      return value.price.times(100).toFixed();
    }

    return value.price.toFixed();
  },
  initialAggFunc: column.initialAggFunc ?? (column.aggregate ? BlotterAggFunc.Price : undefined),
});

function getQuoteCurrency({
  data,
  context,
  security,
  quoteCurrencyField,
}: {
  data: unknown;
  context: BlotterTableAgGridContext;
  security: Security | undefined;
  quoteCurrencyField: PriceParams['quoteCurrencyField'];
}): string | undefined {
  if (get(data, 'PricingMode') === PricingModeEnum.BPS) {
    return 'BPS';
  }

  const quoteCurrency = isFunction(quoteCurrencyField)
    ? quoteCurrencyField(data, context)
    : quoteCurrencyField
      ? get(data, quoteCurrencyField)
      : undefined;

  return quoteCurrency || security?.QuoteCurrency;
}

/**
 * Helps you get the confirm object from the data. Only applies if the security is of type OTC.
 * The user is able to specify exactly what the path is, but we also try to be smart with defaulting and just automatically looking up the ConfirmParam object.
 * Trade-off between speed and risk. I think the benefit is worth it so not every price column ever written has to be explicitly made Confirm-aware.
 */
function getConfirmFromData({
  data,
  security,
  confirmParamsPath,
}: {
  data: unknown;
  security: Security | undefined;
  confirmParamsPath: string | undefined;
}): NonNullable<PriceValue>['confirm'] | undefined {
  if (!security || security.SubProductType !== SubProductTypeEnum.OTCRepo || !isObject(data)) {
    return undefined;
  }

  // Check for Confirm+Type at the top-level properties (but only if a path was not provided)
  if (confirmParamsPath === undefined) {
    const confirm = getConfirmParam({ Confirm: get(data, 'Confirm'), ConfirmType: get(data, 'ConfirmType') });
    if (confirm) {
      return confirm;
    }
  }

  // Default the path to an "established" key that entities can expose a getter for to prepare their ConfirmParam objects.
  const path = confirmParamsPath ?? 'confirmParam';
  const prefix = path ? `${path}.` : '';
  return getConfirmParam({
    Confirm: get(data, `${prefix}Confirm`),
    ConfirmType: get(data, `${prefix}ConfirmType`),
  });
}
